import { useAuth } from '@clerk/clerk-react'
import { useFlag } from '@unleash/proxy-client-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useHeaderNavigation } from '@/hooks/useHeaderNavigation'

export default function Footer() {
  const currentYear = new Date().getFullYear()
  const { t } = useTranslation()
  const { isSignedIn } = useAuth()
  const sitemapEnabled = useFlag('sitemap-footer-nav')
  const { navLinks } = useHeaderNavigation()

  // The brand column doubles as the Home link, so drop the standalone Home entry
  // from the feature columns to avoid a redundant link.
  const featureLinks = navLinks.filter((link) => link.path !== '/')

  return (
    <footer className='relative z-10 bg-transparent mt-auto pt-8 md:pt-16'>
      <div className='border-t border-gray-200'>
        <div className='mx-auto max-w-7xl px-4 py-8 md:px-6'>
          {/* Brand + feature columns — authenticated users only */}
          {isSignedIn && (
            <div className='flex flex-col gap-10 sm:flex-row sm:gap-8'>
              {/* alt='' because the link is already named by aria-label (Home) */}
              <div className='flex flex-col gap-4 sm:w-64 sm:shrink-0'>
                <Link to='/' aria-label={t('header.home')} className='w-fit cursor-pointer'>
                  <img src='/logo512.png' alt='' className='h-14 w-14 rounded-lg' />
                </Link>
                {/* Brand tagline is intentionally English-only (not a translation key) — ENG-3000. */}
                <p className='max-w-[16rem] text-sm text-gray-600'>
                  AI-powered English learning for workforce readiness.
                </p>
              </div>

              {/* Single sitemap landmark; columns adapt to featureLinks.length (2–4) */}
              {sitemapEnabled && featureLinks.length > 0 && (
                <nav
                  aria-label={t('footer.sitemapLabel')}
                  className='grid flex-1 grid-cols-2 gap-x-8 gap-y-10 lg:grid-flow-col lg:auto-cols-fr lg:grid-cols-none'
                >
                  {featureLinks.map((link) => (
                    <div key={link.path}>
                      <h2 className='text-sm font-semibold text-gray-900 mb-2'>
                        <Link to={link.path} className='hover:text-gray-700 cursor-pointer'>
                          {link.label}
                        </Link>
                      </h2>
                      {link.submenu && link.submenu.length > 0 && (
                        <ul className='space-y-1 list-none'>
                          {link.submenu.map((sub) => (
                            <li key={sub.path}>
                              <Link
                                to={sub.path}
                                className='text-sm text-gray-600 transition-colors hover:text-gray-900 cursor-pointer'
                              >
                                {sub.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </nav>
              )}
            </div>
          )}

          {/* Legal bar — divider only when columns are shown above */}
          <div
            className={`flex flex-col gap-6 text-sm text-gray-600 sm:flex-row-reverse sm:gap-3 sm:items-center sm:justify-between ${
              isSignedIn ? 'mt-8 border-t border-gray-200 pt-6' : ''
            }`}
          >
            <nav aria-label={t('footer.legalLabel')}>
              <ul className='flex list-none flex-wrap items-center gap-x-4 gap-y-1'>
                <li>
                  <Link to='/terms-of-use' className='transition-colors hover:text-gray-900 cursor-pointer'>
                    {t('footer.termsOfUse')}
                  </Link>
                </li>
                <li aria-hidden='true' className='text-gray-400'>
                  ·
                </li>
                <li>
                  <Link to='/privacy-policy' className='transition-colors hover:text-gray-900 cursor-pointer'>
                    {t('footer.privacyPolicy')}
                  </Link>
                </li>
                <li aria-hidden='true' className='text-gray-400'>
                  ·
                </li>
                <li>
                  <Link to='/accessibility' className='transition-colors hover:text-gray-900 cursor-pointer'>
                    {t('footer.accessibility')}
                  </Link>
                </li>
              </ul>
            </nav>
            {/* Copyright notice is intentionally English-only (not a translation key) — ENG-3000.
                Last in the DOM so it sits at the very bottom on mobile; row-reverse keeps it
                on the left from sm up. */}
            <p>© {currentYear} EnGen PBC. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  )
}
